import React, {FunctionComponent, useEffect, useRef, useState} from 'react'
import {Button, Dimmer, Dropdown, Form, Grid, Header, Icon, Input, Loader, Message, Modal} from 'semantic-ui-react'
import { FormProps } from 'semantic-ui-react/dist/commonjs/collections/Form/Form'
import placeServiceAPI from '../../apis/PlaceServiceAPI'
import { useAppDispatch } from '../../common/store'
import { NoValue } from '../../common/types'
import {
  ERROR_MESSAGE_PLACENAME_SEARCH_NO_FUTURE_DATE,
  ERROR_MESSAGE_PLACENAME_SEARCH_REGISTERED_DATE_RANGE,
  ERROR_TITLE_PLACENAME_SEARCH_REGISTERED_DATE_RANGE,
} from '../../environment'
import './AdvancedSearchComponent.css'
import { getSearchOptions } from './SearchOptions'
import {
  dropdownOptions,
  getCandidatePlaces,
  resetSearchFilters,
  SearchFilters,
  updatePlacenameSearchValue,
  updateSearchFilters,
} from './slice'

const AdvancedSearchComponent: FunctionComponent<{ showSearchResults(): void; }> = (props) => {
  const initialSearchState = {
    searchTerm: '',
    councils: [],
    localities: [],
    placeTypes: [],
    startDate: '',
    endDate: '',
    namingThemes: [],
    typeDesignations: [],
    status: [],
    fileTypes: [],
    restrictive: true,
  }

  const dispatch = useAppDispatch()

  dispatch(resetSearchFilters())

  const [open, setOpen] = useState(false)
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(initialSearchState)
  const [toMinDate, setToMinDate] = useState<Date | undefined>()
  const [toMaxDate] = useState(new Date())
  const [fromMaxDate, setFromMaxDate] = useState(new Date())
  const [errorTitle, setErrorTitle] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [downloading, setDownloading] = useState(false);

  const advSearch = getSearchOptions()
  const [designationOptions, setDesignationOptions] = useState<dropdownOptions[] | void>()
  const [councilOptions, setCouncilOptions] = useState<dropdownOptions[] | void>()
  const [localityOptions, setLocalityOptions] = useState<dropdownOptions[] | void>()
  const [namingThemeOptions, setNamingThemeOptions] = useState<dropdownOptions[] | void>()
  const toDateInputContainer: React.RefObject<HTMLDivElement> = useRef(null)
  const fromDateInputContainer: React.RefObject<HTMLDivElement> = useRef(null)
  const [fileTypeOptions, setFileTypeOptions] = useState<dropdownOptions[] | void>()
  const errorColor = '#ffe8e6'


  useEffect(() => {
    getSearchOptionFilters()
  }, [])

  useEffect(() => {
    dispatch(updateSearchFilters(searchFilters))
  }, [searchFilters])


  const formatDate = (date: string) => {
    let d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear()

    if (month.length < 2)
      month = '0' + month
    if (day.length < 2)
      day = '0' + day

    return [year, month, day].join('-')
  }

  const formatSearchFilters = (filter: SearchFilters) => {
    if (filter) {
      if (filter.startDate) {
        filter.startDate = formatDate(filter.startDate)
      }
      if (filter.endDate) {
        filter.endDate = formatDate(filter.endDate)
      }
      return filter
    }
  }

  const validateFilterHasValue = (filters: SearchFilters) => {
    for (const key in filters) {
      // @ts-ignore
      const value: string = filters[key]
      if (key !== 'restrictive' && value !== null && (value !== '' || value.length > 0)) {
        return true
      }
    }

    return false
  }

  function handleSubmit() {
    if (searchFilters.startDate && searchFilters.endDate || (!searchFilters.startDate && !searchFilters.endDate)) {
      if (validateFilterHasValue(searchFilters)) {
        const formattedSearchFilter = formatSearchFilters(Object.assign({}, searchFilters))
        dispatch(getCandidatePlaces({ phoneticSearch: false, searchFilters: formattedSearchFilter ? formattedSearchFilter : '' }))
        props.showSearchResults()
        if (searchFilters.searchTerm) {
          dispatch(updatePlacenameSearchValue(searchFilters.searchTerm))
        } else {
          dispatch(updatePlacenameSearchValue(''))
        }
        setOpen(false)
      }

    } else {
      dateRangeError(ERROR_TITLE_PLACENAME_SEARCH_REGISTERED_DATE_RANGE, 'Please complete entry on Registered Date Range')
      setOpen(true)
    }
  }


  function handleChange(e: React.FormEvent<HTMLFormElement>, { name, value }: FormProps) {
    setSearchFilters(prevState => ({
        ...prevState, [name]: value,
      }),
    )
    dispatch(updateSearchFilters({ [name]: value }))
  }

  function handleClearFilter() {
    setSearchFilters(initialSearchState)
    dispatch(resetSearchFilters())
    clearError()
  }

  async function getSearchOptionFilters() {
    setDesignationOptions(await placeServiceAPI.fetchPlacenameSearchOptions('designation'))
    setCouncilOptions(await placeServiceAPI.fetchPlacenameSearchOptions('council'))
    setLocalityOptions(await placeServiceAPI.fetchPlacenameSearchOptions('locality'))
    setNamingThemeOptions(await placeServiceAPI.fetchPlacenameSearchOptions('namingtheme'))
    setFileTypeOptions(await placeServiceAPI.fetchPlacenameSearchOptions('filetypes'))
  }

  function validateFutureDates(d: string, dateInput: HTMLInputElement | NoValue, errorColor: string, fieldName: string) {
    if (!isDateToValid(d)) {
      let fieldLabel = (fieldName === 'endDate' ? 'Registered Date To' : 'Registered Date From')
      setErrorTitle('Invalid ' + fieldLabel)
      setErrorMessage(ERROR_MESSAGE_PLACENAME_SEARCH_NO_FUTURE_DATE + ' \'' + fieldLabel + '\'.')
      if (dateInput) {
        dateInput.style.backgroundColor = errorColor
      }
      setSearchFilters(prevState => ({
        ...prevState, [fieldName]: '',
      }))
      return false
    } else {
      if (dateInput) {
        dateInput.style.backgroundColor = ''
      }
      clearError()
      return true
    }

  }

  function dateRangeError(title: string, message: string) {
    setErrorTitle(title)
    setErrorMessage(message)
    setInputErrorColor()
  }

  function setInputErrorColor() {
    let toDateInput = toDateInputContainer?.current?.getElementsByTagName('input')?.item(0)
    let fromDateInput = fromDateInputContainer?.current?.getElementsByTagName('input').item(0)
    if (toDateInput) {
      toDateInput.style.backgroundColor = errorColor
    }
    if (fromDateInput) {
      fromDateInput.style.backgroundColor = errorColor
    }
  }

  function handleChangeDateRangeHtml5(event: React.ChangeEvent<HTMLInputElement> | undefined ) {
    let toDateInput = toDateInputContainer?.current?.getElementsByTagName('input')?.item(0)
    let fromDateInput = fromDateInputContainer?.current?.getElementsByTagName('input').item(0)

    let name = event?.currentTarget.name!;
    let value = event?.currentTarget.value;

    setSearchFilters(prevState => ({
      ...prevState, [name]: value,
    }))

    if (searchFilters.endDate) {
      setFromMaxDate(new Date(searchFilters.endDate))
    }
    setToMinDate(searchFilters.startDate ? new Date(searchFilters.startDate) : undefined)

    if (searchFilters.startDate && searchFilters.endDate) {
      let d1 = searchFilters.startDate
      let d2 = searchFilters.endDate
      if (d1 || d2) {
        if (fromDateInput && !validateFutureDates(d1, fromDateInput, errorColor, name)) {
          return
        }
        if (toDateInput && !validateFutureDates(d2, toDateInput, errorColor, name)) {
          return
        }
      }
      if (!isDateRangeValid(d1, d2)) {
        dateRangeError(ERROR_TITLE_PLACENAME_SEARCH_REGISTERED_DATE_RANGE, ERROR_MESSAGE_PLACENAME_SEARCH_REGISTERED_DATE_RANGE)
        setSearchFilters(prevState => ({
          ...prevState, startDate: '', endDate: '',
        }))
        return
      } else {
        clearError()

      }
    } else if (searchFilters[name as keyof SearchFilters]) {
      let d = searchFilters[name as keyof SearchFilters] as string
      let dateInput = (name === 'endDate' ? toDateInput : fromDateInput)
      validateFutureDates(d, dateInput, errorColor, name)
    }
    dispatch(updateSearchFilters(searchFilters))
  }

  function isFilterNotEmpty(filter: SearchFilters) {
    return (validateFilterHasValue(filter))
  }

  function clearError() {
    let toDateInput = toDateInputContainer.current?.getElementsByTagName('input').item(0)
    let fromDateInput = fromDateInputContainer.current?.getElementsByTagName('input').item(0)
    if (toDateInput) {
      toDateInput.style.backgroundColor = ''
    }
    if (fromDateInput) {
      fromDateInput.style.backgroundColor = ''
    }
    setErrorTitle('')
    setErrorMessage('')
  }

  function isDateRangeValid(date1: string, date2: string) {
    const d1 = Date.parse(date1)
    const d2 = Date.parse(date2)
    return !(d1 > d2 || d2 > Date.now())
  }

  function isDateToValid(date: string) {
    const d = Date.parse(date)
    return d <= Date.now()
  }

  function onCloseModal() {
    clearError()
    dispatch(updateSearchFilters(searchFilters))
    setOpen(false)
  }

  const downloadExcel = async () => {
    setDownloading(true)
    let xls = await placeServiceAPI.fetchReport(searchFilters)
    if (xls) {
      let fileName = 'placename-data-extract.xlsx'
      let blob = new Blob([xls], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      let data = URL.createObjectURL(blob)
      let link = document.createElement('a')
      link.download = fileName
      link.href = data
      link.click()
      URL.revokeObjectURL(data)
      setDownloading(false)
      return true
    }
    return false
  }


  return (
    <Modal
      className={'searchModal'}
      centered={false}
      open={open}
      closeIcon
      onClose={onCloseModal}
      trigger={
        <div className={'advancedSearchBtn'}>
          <Button compact size="tiny" primary>Advanced Search</Button>
        </div>
      }
      onOpen={() => setOpen(true)}
    >
      <Header content="Advanced Search"/>
      <Modal.Content>
        {(isFilterNotEmpty(searchFilters)) &&
            <Button
                className={'clearFilter'}
                icon="close"
                content="Clear"
                onClick={handleClearFilter}
            />}

        <Form size="tiny" onSubmit={handleSubmit} className={'searchForm'} autoComplete="off">
          <Button disabled={!isFilterNotEmpty(searchFilters)} className={'searchModalAction'} primary>
            <Icon name="search"/> Search
          </Button>

          <Grid columns={2} stackable>
            {/*1st Row*/}
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field control={Input} label="Name" name="searchTerm" value={searchFilters.searchTerm}
                            placeholder="Search..." onChange={handleChange}/>
              </Grid.Column>
            </Grid.Row>
            {/*2nd Row*/}
            <Grid.Row columns={2}>
              <Grid.Column>
                <div ref={fromDateInputContainer}>
                  <Form.Field className={'datePicker'}>

                    <label htmlFor={"fromDate"}>Registered Date From</label>
                    <input type={"date"}
                           name={"startDate"}
                           value={searchFilters.startDate ? searchFilters.startDate : ""}
                           onChange={handleChangeDateRangeHtml5}
                    />
                  </Form.Field>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div ref={toDateInputContainer}>
                  <Form.Field className={'datePicker'}>
                    <label htmlFor={"toDate"}>Registered Date To</label>
                    <input type={"date"}
                           name={"endDate"}
                           value={searchFilters.endDate ? searchFilters.endDate : ""}
                           onChange={handleChangeDateRangeHtml5}
                    />

                  </Form.Field>
                </div>
              </Grid.Column>
            </Grid.Row>
            {/*3rd Row*/}
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field
                    control={Dropdown}
                    name="placeTypes"
                    value={searchFilters.placeTypes}
                    label="Place Type"
                  placeholder="Place Type"
                  fluid
                  multiple
                  search
                  selection
                  options={advSearch.categoryOptions}
                  onChange={handleChange}
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Field
                  control={Dropdown}
                  name="typeDesignations"
                  value={searchFilters.typeDesignations}
                  label="Type Designation"
                  placeholder="Type Designation"
                  fluid
                  multiple
                  search
                  selection
                  loading={!designationOptions}
                  options={designationOptions || []}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
            {/*4th Row*/}
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field
                  control={Dropdown}
                  name="councils"
                  value={searchFilters.councils}
                  label="Council"
                  placeholder="Council"
                  fluid
                  multiple
                  search
                  selection
                  loading={!councilOptions}
                  options={councilOptions || []}
                  onChange={handleChange}
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Field
                  control={Dropdown}
                  name="localities"
                  value={searchFilters.localities}
                  label="Locality"
                  placeholder="Locality"
                  fluid
                  multiple
                  search
                  selection
                  loading={!localityOptions}
                  options={localityOptions || []}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
            {/*5th Row*/}
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field
                  control={Dropdown}
                  name="namingThemes"
                  value={searchFilters.namingThemes}
                  label="Naming Theme"
                  placeholder="Naming Theme"
                  fluid
                  multiple
                  search
                  selection
                  loading={!namingThemeOptions}
                  options={namingThemeOptions || []}
                  onChange={handleChange}
                />
              </Grid.Column>
              <Grid.Column>
                <Form.Field
                  control={Dropdown}
                  name="status"
                  value={searchFilters.status}
                  label="Status"
                  placeholder="Status"
                  fluid
                  multiple
                  search
                  selection
                  options={advSearch.statusOptions}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
            {/* 6th row*/}
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field
                  control={Dropdown}
                  name="fileTypes"
                  value={searchFilters.fileTypes}
                  label="File Type"
                  placeholder="File Type"
                  fluid
                  multiple
                  search
                  selection
                  loading={!fileTypeOptions}
                  options={fileTypeOptions || []}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
            {/* 7th row for the report saving options */}
            {isFilterNotEmpty(searchFilters) &&
                <Grid.Row columns={1}>
                    <Grid.Column>
                        <p>Download Results As</p>
                        <ul className="downloadLinks">
                            <li className="downloadLinks">
                                <a onClick={downloadExcel} title="Excel" style={{ cursor: 'pointer' }}>
                                    <i className="file excel icon large green"></i> Excel</a>
                            </li>
                        </ul>
                      <Dimmer active={downloading} inverted>
                        <Loader inverted size='small'>Downloading</Loader>
                      </Dimmer>
                    </Grid.Column>
                </Grid.Row>
            }
          </Grid>
        </Form>
        {(errorTitle) &&
            <Message error>
                <Message.Header>{errorTitle}</Message.Header>
                <p>{errorMessage}</p>
            </Message>}
      </Modal.Content>
    </Modal>
  )
}


export default AdvancedSearchComponent